/**
 * Acknowledges the selected OncoEMR task after its messages are visible.
 * @module useOncoemrTaskAck
 */

import { useEffect, useRef } from "react";

import { isOncoemrTaskAckStaleError } from "../../../../../api/postCall/oncoemrTaskAckErrors";
import { ackOncoemrTaskWithStaleRetry } from "../helpers/ackOncoemrTaskWithStaleRetry";

/** Inputs for {@link useOncoemrTaskAck}. */
export interface UseOncoemrTaskAckParams {
  orgId: string;
  taskRowId?: string;
  expectedVersion?: number;
  isUpdated?: boolean;
  /** True when the conversation panel has finished loading messages without error. */
  messagesReady: boolean;
  /** Applies successful ack to the in-memory task list. */
  onAcknowledged: (taskRowId: string, version: number) => void;
  /** Refetches tasks after stale ack or missing row. */
  onRefetchTasks: () => void;
}

/**
 * Calls `POST .../ack` once per selection when the user views an updated task thread.
 *
 * @param params - Selected task metadata, message readiness, and list update callbacks.
 */
export const useOncoemrTaskAck = ({
  orgId,
  taskRowId,
  expectedVersion,
  isUpdated,
  messagesReady,
  onAcknowledged,
  onRefetchTasks,
}: UseOncoemrTaskAckParams): void => {
  const ackedForSelectionRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    abortRef.current?.abort();
    ackedForSelectionRef.current = null;
  }, [taskRowId]);

  useEffect(() => {
    if (
      !orgId ||
      !taskRowId ||
      !messagesReady ||
      !isUpdated ||
      expectedVersion === undefined
    ) {
      return undefined;
    }

    if (ackedForSelectionRef.current === taskRowId) {
      return undefined;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    const runAck = async () => {
      try {
        const result = await ackOncoemrTaskWithStaleRetry(
          taskRowId,
          orgId,
          expectedVersion,
          controller.signal,
        );

        if (controller.signal.aborted) {
          return;
        }

        ackedForSelectionRef.current = taskRowId;
        onAcknowledged(taskRowId, result.version);
      } catch (error) {
        if (controller.signal.aborted || error instanceof DOMException) {
          return;
        }

        if (isOncoemrTaskAckStaleError(error)) {
          onRefetchTasks();
          return;
        }

        if (error instanceof Error && /\(404\)/.test(error.message)) {
          onRefetchTasks();
        }
      }
    };

    void runAck();

    return () => {
      controller.abort();
    };
  }, [
    orgId,
    taskRowId,
    expectedVersion,
    isUpdated,
    messagesReady,
    onAcknowledged,
    onRefetchTasks,
  ]);
};
